import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { useResolveBrokenSensorsAlert } from '../../../alert-resolution/composables/use-resolve-broken-sensors-alert';
import type { InterventionCreateRequest } from '../../../interventions/models/intervention-create-request';
import { PendingInterventionCreateRequest } from '../../../interventions/models/pending-intervention-create-request';
import { HttpInterventionGateway } from '../../../interventions/services/intervention-gateway.service';
import { InterventionActions } from '../../../interventions/store/actions/interventions';
import { DEVICE_COMMANDS, DeviceMS, FailureType } from '../../../site-detail/models/Device';
import {
    DeviceStateUpdateExecutionInfo,
    Execution,
    ExecutionInfo,
    ExecutionStatus,
} from '../../../site-detail/models/Execution';
import { SectionExecutionStatus } from '../../../site-detail/models/SectionExecutionInfo';
import { Site } from '../../../site-detail/models/Site';
import { UiProfile } from '../../../site-detail/models/UiProfile';
import { DEVICE_GATEWAY } from '../../../site-detail/services/DeviceGateway';
import { SiteDetailPageActions } from '../../../site-detail/store/actions/site-detail-page.actions';
import { SiteDevicesActions } from '../../../site-detail/store/actions/site-devices.actions';
import {
    removePendingInterventionAction,
    updateDeviceStateToSyncExecutionStatusById,
    updateExecutionStatus,
    updateManualExecution,
} from '../../../site-detail/store/actions/ui.actions';
import { UIDeviceDefinitionState } from '../../../site-detail/store/reducers/ui.devicedefinitions';
import { getSelectedSiteDevices } from '../../../site-detail/store/selectors/devices';
import {
    getUiDeviceDefinitions,
    getUIDevicesSectionsSync,
    getUIDevicesStatesSync,
    getUIManualExecutions,
    getUIPendingInterventionActions,
    getUISiteSectionExecutionInfo,
} from '../../../site-detail/store/selectors/ui';
import { getSites } from '../../../site-list/store/selectors/sites';
import { getOwnerFullName } from '../../../site-owner/utils/owner.util';
import { useToaster } from '../../composables/use-toaster';
import { UISocketDataMessage } from '../../models/UISocket';
import { AppState } from '../../store/app-state';
import { SocketAction, SocketActionHandler } from '../SocketActionHandler';

interface UISocketExecutionUpdateMessage extends UISocketDataMessage {
    job_id: string;
    status: ExecutionStatus;
    failureType?: string;
}

@Injectable()
export class ExecutionStateChangedHandler implements SocketActionHandler {
    readonly action: SocketAction = 'executionStateChanged';

    private readonly deviceGateway = inject(DEVICE_GATEWAY);
    private readonly interventionService = inject(HttpInterventionGateway);
    private readonly store = inject<Store<AppState>>(Store);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly toaster = useToaster();
    private readonly resolveBrokenSensorsAlert = useResolveBrokenSensorsAlert();

    handle(message: UISocketExecutionUpdateMessage): void {
        combineLatest([
            this.store.select(getUIDevicesSectionsSync),
            this.store.select(getUIDevicesStatesSync),
            this.store.select(getUIManualExecutions),
            this.store.select(getSites),
            this.store.select(getSelectedSiteDevices),
            this.store.select(getUiDeviceDefinitions),
            this.store.select(getUIPendingInterventionActions),
        ])
            .pipe(take(1))
            .subscribe(
                ([
                    devicesSectionsSync,
                    devicesStatesSync,
                    manualExecutions,
                    sites,
                    devices,
                    uiDeviceDefinitions,
                    pendingInterventionActions,
                ]) => {
                    // Look for execution in list of device section sync
                    const executionsInfoForSectionRefresh = devicesSectionsSync.filter(
                        (exec) => exec.job_id === message.job_id,
                    );

                    // Look for execution in list of device state sync
                    const deviceStateUpdateExecution = devicesStatesSync.find((exec) => exec.job_id === message.job_id);

                    const manualExecutionExec = manualExecutions.find((exec) => exec.job_id === message.job_id);

                    if (executionsInfoForSectionRefresh && executionsInfoForSectionRefresh.length > 0) {
                        this.store.dispatch(
                            updateExecutionStatus({
                                execution: {
                                    status: message.status,
                                    job_id: message.job_id,
                                },
                            }),
                        );
                        this.dispatchSectionRefreshOutcomeIfFinalized(executionsInfoForSectionRefresh[0]);

                        if (message.status === ExecutionStatus.QUEUED_GATEWAY_SIDE) {
                            // put a 2 min timer
                            setTimeout(() => {
                                const executionsStandByInfoForSectionRefresh = devicesSectionsSync.filter(
                                    (exec) => exec.job_id === message.job_id,
                                );
                                if (
                                    executionsStandByInfoForSectionRefresh &&
                                    executionsStandByInfoForSectionRefresh.length > 0
                                ) {
                                    this.store.dispatch(
                                        updateExecutionStatus({
                                            execution: {
                                                status: ExecutionStatus.FAILED,
                                                job_id: message.job_id,
                                            },
                                        }),
                                    );
                                    this.dispatchSectionRefreshOutcomeIfFinalized(
                                        executionsStandByInfoForSectionRefresh[0],
                                    );
                                    executionsStandByInfoForSectionRefresh.map(
                                        (executionStandByInfoForSectionRefresh: ExecutionInfo) => {
                                            this.verifyDefinitionAndSendStateStateTaosterMessage(
                                                sites,
                                                devices,
                                                uiDeviceDefinitions,
                                                executionStandByInfoForSectionRefresh,
                                                true,
                                            );
                                        },
                                    );
                                }
                            }, 120 * 1000);
                        } else if (
                            message.status === ExecutionStatus.FAILED &&
                            message.failureType !== FailureType.CMDDEPRECATED
                        ) {
                            // Format message for toaster
                            executionsInfoForSectionRefresh.map((executionInfoForSectionRefresh: ExecutionInfo) => {
                                this.verifyDefinitionAndSendStateStateTaosterMessage(
                                    sites,
                                    devices,
                                    uiDeviceDefinitions,
                                    executionInfoForSectionRefresh,
                                );
                            });
                        }
                    } else if (deviceStateUpdateExecution) {
                        const site: Site = sites[deviceStateUpdateExecution.siteId];
                        if (!site) {
                            return;
                        }
                        const device: DeviceMS = devices.find((d) => d.id === deviceStateUpdateExecution.deviceId);
                        if (!device) {
                            return;
                        }
                        if (message.status === ExecutionStatus.QUEUED_GATEWAY_SIDE) {
                            // put a 2 min timer
                            setTimeout(() => {
                                const deviceStandByExecution = devicesStatesSync.find(
                                    (exec) => exec.job_id === message.job_id,
                                );
                                if (deviceStandByExecution) {
                                    this.store.dispatch(
                                        updateDeviceStateToSyncExecutionStatusById({
                                            execution: {
                                                status: ExecutionStatus.FAILED,
                                                job_id: message.job_id,
                                            },
                                        }),
                                    );
                                    this.saveCompletedDeviceAction(pendingInterventionActions, message, true);
                                    this.verifyDefinitionAndSendStateUpdateTaosterMessage(
                                        sites,
                                        devices,
                                        uiDeviceDefinitions,
                                        deviceStateUpdateExecution,
                                        true,
                                    );
                                }
                            }, 120 * 1000);
                        } else if (
                            message.status === ExecutionStatus.FAILED &&
                            message.failureType !== FailureType.CMDDEPRECATED
                        ) {
                            this.verifyDefinitionAndSendStateUpdateTaosterMessage(
                                sites,
                                devices,
                                uiDeviceDefinitions,
                                deviceStateUpdateExecution,
                            );
                        } else if (message.status === ExecutionStatus.COMPLETED) {
                            this.saveCompletedDeviceAction(pendingInterventionActions, message);
                        }
                        this.store.dispatch(
                            updateDeviceStateToSyncExecutionStatusById({
                                execution: {
                                    status: message.status,
                                    job_id: message.job_id,
                                },
                            }),
                        );
                    } else if (manualExecutionExec) {
                        this.handleManualExecutionStateChanged(manualExecutionExec, message);
                    }
                },
            );
    }

    private dispatchSectionRefreshOutcomeIfFinalized(execution: ExecutionInfo): void {
        this.store
            .select(getUISiteSectionExecutionInfo)
            .pipe(take(1))
            .subscribe((siteSectionExecutionInfo) => {
                const section = siteSectionExecutionInfo?.[execution.siteId]?.[execution.deviceId]?.find(
                    (s) => s.section === execution.section,
                );
                if (!section) return;

                const payload = {
                    siteId: execution.siteId,
                    deviceId: execution.deviceId,
                    sectionTitle: execution.section,
                };

                if (section.status === SectionExecutionStatus.COMPLETED) {
                    this.store.dispatch(SiteDetailPageActions.sectionRefreshCompleted(payload));
                }
                if (section.status === SectionExecutionStatus.FAILED && section.pendingRefresh === 0) {
                    this.store.dispatch(SiteDetailPageActions.sectionRefreshAborted(payload));
                }
            });
    }

    private handleManualExecutionStateChanged(manualExecution: Execution, message: UISocketExecutionUpdateMessage) {
        const executionUpdated: Execution = {
            job_id: manualExecution.job_id,
            deviceId: manualExecution.deviceId,
            state: manualExecution.state,
            // we update status with the new one
            status: message.status,
            failureType: message.failureType !== FailureType.NO_FAILURE ? message.failureType : undefined,
        };

        if (executionUpdated.state === DEVICE_COMMANDS.REMOVE_BROKEN_SENSORS) {
            if (executionUpdated.status === ExecutionStatus.COMPLETED) {
                this.store.dispatch(
                    SiteDevicesActions.removeBrokenSensorsSucceed({ deviceId: manualExecution.deviceId }),
                );
                if (manualExecution.deviceId) {
                    void this.resolveBrokenSensorsAlert.resolve(manualExecution.deviceId);
                }
            }

            if (executionUpdated.status === ExecutionStatus.FAILED) {
                this.store.dispatch(
                    SiteDevicesActions.removeBrokenSensorsFailed({ deviceId: manualExecution.deviceId }),
                );
            }
        }

        this.store.dispatch(updateManualExecution({ exec: executionUpdated }));
    }

    private verifyDefinitionAndSendStateStateTaosterMessage(
        sites: Record<string, Site>,
        devices: DeviceMS[],
        uiDeviceDefinitions: UIDeviceDefinitionState,
        executionInfo: ExecutionInfo,
        isQueued = false,
    ) {
        const site: Site = sites[executionInfo.siteId];
        if (!site) {
            return;
        }
        const device: DeviceMS = devices.find((d) => d.id === executionInfo.deviceId);
        if (!device) {
            return;
        }
        if (device.type in uiDeviceDefinitions) {
            this.sendDeviceStateRefreshFailedOrQueuedMessage(
                site,
                device,
                uiDeviceDefinitions[device.type],
                executionInfo,
                isQueued,
            );
        } else {
            // Get device definition from rest api
            this.deviceGateway.getDeviceUI(device.uiType).subscribe((definition) => {
                this.sendDeviceStateRefreshFailedOrQueuedMessage(site, device, definition, executionInfo, isQueued);
            });
        }
    }

    private verifyDefinitionAndSendStateUpdateTaosterMessage(
        sites: Record<string, Site>,
        devices: DeviceMS[],
        uiDeviceDefinitions: UIDeviceDefinitionState,
        executionInfo: DeviceStateUpdateExecutionInfo,
        isQueued = false,
    ) {
        const site: Site = sites[executionInfo.siteId];
        if (!site) {
            return;
        }
        const device: DeviceMS = devices.find((d) => d.id === executionInfo.deviceId);
        if (!device) {
            return;
        }
        if (device.type in uiDeviceDefinitions) {
            this.sendDeviceStateUpdateFailedOrQueuedMessage(
                site,
                device,
                uiDeviceDefinitions[device.type],
                executionInfo,
                isQueued,
            );
        } else {
            // Get device definition from rest api
            this.deviceGateway.getDeviceUI(device.uiType).subscribe((definition) => {
                this.sendDeviceStateUpdateFailedOrQueuedMessage(site, device, definition, executionInfo, isQueued);
            });
        }
    }

    private sendDeviceStateUpdateFailedOrQueuedMessage(
        site: Site,
        device: DeviceMS,
        deviceDefinitionUI: UiProfile,
        executionInfo: DeviceStateUpdateExecutionInfo,
        queued = false,
    ) {
        const uiProperty = deviceDefinitionUI.properties[executionInfo.state];
        const stateLabel = this.translateService.instant(uiProperty ? uiProperty.label : 'unknown');

        this.toaster.error({
            title: device['name'],
            subtitle: this.translateService.instant(
                queued ? 'TOASTER_DEVICE_UPSATE_STATE_VALID_QUEUED' : 'TOASTER_DEVICE_UPSATE_STATE_INVALID',
                { stateLabel },
            ),
            message: getOwnerFullName(site.owners[0]),
            onClick: () =>
                this.router.navigate([`/gateway/${site.gatewayId}`], { queryParams: { deviceId: device.id } }),
            duration: 45 * 1000, // how long do we want to display the error toaster
        });
    }

    private sendDeviceStateRefreshFailedOrQueuedMessage(
        site: Site,
        device: DeviceMS,
        deviceDefinitionUI: UiProfile,
        executionInfo: ExecutionInfo,
        queued = false,
    ) {
        const uiProperty = deviceDefinitionUI.properties[executionInfo.state];
        const stateLabel = this.translateService.instant(uiProperty ? uiProperty.label : 'unknown');

        this.toaster.error({
            title: device['name'],
            subtitle: this.translateService.instant(
                queued ? 'TOASTER_DEVICE_REFRESH_STATE_VALID_QUEUED' : 'TOASTER_DEVICE_REFRESH_STATE_INVALID',
                { stateLabel },
            ),
            message: getOwnerFullName(site.owners[0]),
            onClick: () =>
                this.router.navigate([`/gateway/${site.gatewayId}`], { queryParams: { deviceId: device.id } }),
            duration: 15 * 1000, // how long do we want to display the error toaster
        });
    }

    private saveCompletedDeviceAction(
        pendingInterventionActions: PendingInterventionCreateRequest[],
        message: UISocketExecutionUpdateMessage,
        queued = false,
    ) {
        const pendingAction = pendingInterventionActions.find((a) => a.job_id === message.job_id);
        const newIntervention: InterventionCreateRequest = pendingAction;
        if (queued) {
            newIntervention.data.queued = true;
        }
        this.interventionService.createInterventionEvent(newIntervention).subscribe((updatedIntervention) => {
            this.store.dispatch(
                removePendingInterventionAction({
                    interventionAction: pendingAction,
                }),
            );
            if (!queued) {
                this.updateSiteDevice(pendingAction.id_site, pendingAction.id_device);
            }
            this.store.dispatch(InterventionActions.updateSiteIntervention({ intervention: updatedIntervention }));
        });
    }

    private updateSiteDevice(id_site: string, id_device: string) {
        // TODO needs refacto; reload full device to pass through value conversion/transform/... defined in
        //  DeviceDetailComponent:updateDeviceDefinition(), so value is correctly store in store;
        //  refacto must go further by rethink and normalize store AND move all business conversion in
        //  MS Device ; transforms for humans reading purpose are only behaviors that must be kept here
        this.deviceGateway.getDeviceById(id_device).subscribe((newDevice) => {
            this.store.dispatch(SiteDevicesActions.deviceUpdated({ siteId: id_site, device: newDevice }));
        });
    }
}
